import { useState, useEffect } from 'react';
import useActivityStatus from './useActivityStatus';
import useCachedRead from './useCachedRead';
import { getPeerActivity } from '../services/activityService';
import {
  getConnectionLocations,
  getLocationSharingEnabled,
} from '../services/location/liveLocationService';
import { getProfileUserId } from '../utils/identityUtils';

/**
 * usePeerStatus — fetches a peer's activity status and live location for status subtitles.
 *
 * Consolidates the near-identical fetch logic that lived in the chat headers
 * (ProfileConnectionPage, AnonConnectionPage) and the detail pages
 * (ProfileConnectionDetailPage, AnonConnectionDetailPage). Each surface resolves
 * the peer's profile user id from its own data shape and passes it in.
 *
 * The peer's live location paints instantly from the shared
 * `connection_locations_cache` (last-known value; whole-map cache, so any page
 * warms every connection) and silently corrects when the RPC resolves — the
 * user-accepted stale-while-revalidate behavior (db-cached-reads, 2026-07-06).
 *
 * @param {string|null} peerProfileUserId - Peer's profile user id (from anonData.senderProfileUserId
 *   or profileData.profileUserId, resolved by the caller)
 * @param {string|null} chatRoomId - Chat room id, used to look up the peer's shared location
 * @param {object} [options]
 * @param {boolean|null} [options.peerOnline=null] - Heartbeat presence (chat pages only). null on
 *   detail pages — they have no heartbeat, so "Active now" never comes from here.
 * @param {boolean} [options.fetchLocation=true] - Whether to fetch live location. false for anon
 *   users, who don't see peer location.
 * @returns {{
 *   activityStatus: ({text: string, isActive: boolean}|null),
 *   peerLocation: string|null,
 *   peerLocationRefreshing: boolean
 * }}
 */
const usePeerStatus = (
  peerProfileUserId,
  chatRoomId,
  { peerOnline = null, fetchLocation = true } = {}
) => {
  const [peerLastActiveAt, setPeerLastActiveAt] = useState(null);
  const activityStatus = useActivityStatus(peerOnline, peerLastActiveAt);

  useEffect(() => {
    if (peerProfileUserId) {
      getPeerActivity(peerProfileUserId).then(setPeerLastActiveAt);
    }
  }, [peerProfileUserId]);

  // Live location map for all connections — one RPC, one shared cache key.
  // Reciprocity: only fetched (and only cache-painted) when the viewer shares.
  const { data: locationMap, refreshing: peerLocationRefreshing } = useCachedRead(
    'connection_locations_cache',
    () => getConnectionLocations(getProfileUserId()),
    { enabled: Boolean(fetchLocation && getProfileUserId() && getLocationSharingEnabled()) }
  );

  const peerLocation = (locationMap && locationMap[chatRoomId]) || null;

  return { activityStatus, peerLocation, peerLocationRefreshing };
};

export default usePeerStatus;
